import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, Loader2, Instagram } from "lucide-react";
import { BRAND, PROPERTY_TYPES } from "@/lib/content";
import { useTexts, useExtras } from "@/lib/texts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const INITIAL = { name: "", email: "", phone: "", property_type: PROPERTY_TYPES[0], budget: "", message: "" };

const inputCls =
    "w-full border border-stone-900/15 bg-white px-4 py-3.5 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-[#E75C15]";

export const Contact = () => {
    const texts = useTexts();
    const { instagram } = useExtras();
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Compila nome, email e messaggio per inviare la richiesta.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Richiesta inviata. Ti ricontatteremo entro 24 ore lavorative.");
            setForm(INITIAL);
        } catch (err) {
            toast.error("Invio non riuscito. Riprova o scrivici via email.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contatti" data-testid="contact-section" className="bg-[#F9F7F2] py-20 sm:py-28 lg:py-36">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
                <div className="lg:col-span-5">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#C24E0F]"
                    >
                        Contatti
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        className="mt-4 font-display text-3xl font-light tracking-tight text-stone-900 sm:text-4xl lg:text-5xl"
                    >
                        {texts.contact_heading}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                        className="mt-6 max-w-md text-base leading-relaxed text-stone-600"
                    >
                        {texts.contact_paragraph}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
                        data-testid="contact-info-card"
                        className="mt-10 space-y-5 border border-stone-900/10 bg-[#1C1917] p-8 text-stone-100"
                    >
                        <a
                            href={`mailto:${BRAND.email}`}
                            data-testid="contact-info-email"
                            className="flex items-center gap-4 transition-colors hover:text-[#E75C15]"
                        >
                            <Mail className="h-5 w-5 shrink-0 text-[#E75C15]" />
                            <span className="text-sm">{BRAND.email}</span>
                        </a>
                        <a
                            href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                            data-testid="contact-info-phone"
                            className="flex items-center gap-4 transition-colors hover:text-[#E75C15]"
                        >
                            <Phone className="h-5 w-5 shrink-0 text-[#E75C15]" />
                            <span className="text-sm">{BRAND.phone}</span>
                        </a>
                        <div className="flex items-center gap-4" data-testid="contact-info-address">
                            <MapPin className="h-5 w-5 shrink-0 text-[#E75C15]" />
                            <span className="text-sm">{BRAND.address}</span>
                        </div>
                        {instagram && (
                            <a
                                href={instagram}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-instagram-link"
                                className="flex items-center gap-4 transition-colors hover:text-[#E75C15]"
                            >
                                <Instagram className="h-5 w-5 shrink-0 text-[#E75C15]" />
                                <span className="text-sm">Seguici su Instagram</span>
                            </a>
                        )}
                        <div className="flex items-center gap-4" data-testid="contact-info-hours">
                            <Clock className="h-5 w-5 shrink-0 text-[#E75C15]" />
                            <span className="text-sm">{BRAND.hours}</span>
                        </div>
                    </motion.div>
                </div>

                <motion.form
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="lg:col-span-7"
                >
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                            <label htmlFor="contact-name" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                Nome e Cognome *
                            </label>
                            <input
                                id="contact-name"
                                data-testid="contact-input-name"
                                className={inputCls}
                                placeholder="Mario Rossi"
                                value={form.name}
                                onChange={set("name")}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="contact-email" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                Email *
                            </label>
                            <input
                                id="contact-email"
                                type="email"
                                data-testid="contact-input-email"
                                className={inputCls}
                                placeholder="mario@esempio.it"
                                value={form.email}
                                onChange={set("email")}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="contact-phone" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                Telefono
                            </label>
                            <input
                                id="contact-phone"
                                data-testid="contact-input-phone"
                                className={inputCls}
                                placeholder="+39 ..."
                                value={form.phone}
                                onChange={set("phone")}
                            />
                        </div>
                        <div>
                            <label htmlFor="contact-budget" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                Budget Indicativo
                            </label>
                            <input
                                id="contact-budget"
                                data-testid="contact-input-budget"
                                className={inputCls}
                                placeholder="es. 150.000 – 300.000 €"
                                value={form.budget}
                                onChange={set("budget")}
                            />
                        </div>
                    </div>

                    <div className="mt-6">
                        <span className="mb-3 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                            Tipologia di Intervento
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {PROPERTY_TYPES.map((t) => (
                                <button
                                    type="button"
                                    key={t}
                                    data-testid={`contact-type-${t.split(" ")[0].toLowerCase()}`}
                                    onClick={() => setForm((f) => ({ ...f, property_type: t }))}
                                    className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                                        form.property_type === t
                                            ? "border-[#1C1917] bg-[#1C1917] text-stone-100"
                                            : "border-stone-900/20 text-stone-600 hover:border-[#E75C15] hover:text-stone-900"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-6">
                        <label htmlFor="contact-message" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                            Messaggio *
                        </label>
                        <textarea
                            id="contact-message"
                            data-testid="contact-input-message"
                            rows={5}
                            className={`${inputCls} resize-none`}
                            placeholder="Descrivici l'immobile e l'intervento che hai in mente..."
                            value={form.message}
                            onChange={set("message")}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={sending}
                        data-testid="contact-form-submit-button"
                        className="group mt-8 inline-flex items-center gap-3 rounded-full bg-[#1C1917] px-8 py-4 text-sm font-medium text-stone-100 transition-colors duration-300 hover:bg-[#B8430D] disabled:opacity-60"
                    >
                        {sending ? (
                            <>
                                Invio in corso
                                <Loader2 className="h-4 w-4 animate-spin" />
                            </>
                        ) : (
                            <>
                                Invia la Richiesta
                                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </>
                        )}
                    </button>
                </motion.form>
            </div>
        </section>
    );
};
